import { Component, useEffect, useRef, type ErrorInfo, type ReactNode } from 'react'
import { useRouteError } from 'react-router-dom'
import { supabase } from '@/services/supabase/client'

/**
 * The net under the trapeze (§auditoría H80).
 *
 * Before this existed, any exception while drawing a screen left the app as a
 * white page: no message, no way back, nothing recorded. Now the person sees
 * something human with two ways out, and the failure is written to app_events
 * so somebody can find out it happened at all.
 */
function report(error: unknown, where: string) {
  try {
    const e = error as { message?: string; stack?: string }
    void supabase
      .rpc('log_app_event', {
        p_kind: 'client_error',
        p_detail: where,
        p_context: {
          message: String(e?.message ?? error).slice(0, 300),
          stack: String(e?.stack ?? '').slice(0, 900),
          path: window.location.pathname,
          ua: navigator.userAgent.slice(0, 160),
        },
      })
      .then(() => undefined, () => undefined)
  } catch {
    /* a crash report must never crash */
  }
}

function Fallen({ onRetry }: { onRetry: () => void }) {
  return (
    <main className="page page--nodock">
      <div className="empty" style={{ minHeight: '70svh' }}>
        <div className="empty__mount" />
        <h1 className="empty__title">Se nos ha escapado el gato</h1>
        <p className="empty__body">
          Algo se ha roto al dibujar esta pantalla. No has perdido nada: tus gatos, tus fotos y tus
          encuentros siguen guardados.
        </p>
        <div className="stack stack--2" style={{ marginBlockStart: 'var(--s5)', width: 'min(100%, 320px)' }}>
          <button type="button" className="btn btn--primary btn--lg" onClick={onRetry}>
            Volver a intentarlo
          </button>
          <a className="btn btn--secondary" href="/">
            Ir al inicio
          </a>
        </div>
      </div>
    </main>
  )
}

/** Catches render errors anywhere below it. */
export class ErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    report(error, 'render:' + String(info.componentStack ?? '').slice(0, 80))
    console.error('[catgo] render error', error)
  }

  render() {
    if (this.state.failed) return <Fallen onRetry={() => window.location.reload()} />
    return this.props.children
  }
}

/** The router's own error slot: a broken route, a lazy chunk that will not load. */
export function RouteErrorScreen() {
  const error = useRouteError()
  // Se avisa una vez, y fuera del dibujado: React puede repetir un render, y
  // un mismo fallo contado cinco veces se come el cupo del registro.
  const avisado = useRef(false)
  useEffect(() => {
    if (avisado.current) return
    avisado.current = true
    report(error, 'route')
  }, [error])
  return <Fallen onRetry={() => window.location.reload()} />
}
